import React, { useState, useEffect, useRef } from 'react';
import { Checklist, ChecklistItem, ChecklistVariable, ChecklistItemEvidenceDefinition, EvidenceFieldDefinition } from '@/firebase/checklistService';
import { ComplianceTemplate, complianceService } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { assetService, AssetType } from '@/firebase/assetService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { X, Plus, Search, ChevronDown, ChevronRight, Edit2, Trash2, Download } from 'lucide-react';

// Form draft type: keywords/anchors can be string (raw input) or string[] (saved) so spaces are allowed while typing
type FieldFormExtractionHints = { keywords?: string | string[]; anchors?: string | string[]; regex?: string };
type NewFieldDraft = Omit<Partial<EvidenceFieldDefinition>, 'extractionHints' | 'fileNameKeys' | 'enumValues'> & { extractionHints?: FieldFormExtractionHints; fileNameKeys?: string | string[]; enumValues?: string | string[] };
type FieldEditFormData = Omit<EvidenceFieldDefinition, 'extractionHints' | 'fileNameKeys' | 'enumValues'> & { extractionHints?: FieldFormExtractionHints; fileNameKeys?: string | string[]; enumValues?: string | string[] };

interface ChecklistFormProps {
  checklist?: Checklist;
  checklists?: Checklist[]; // List of all checklists for "Copy From" dropdown
  onSubmit: (data: Partial<Checklist>) => void;
  onCancel: () => void;
}

const ChecklistForm: React.FC<ChecklistFormProps> = ({ checklist, checklists = [], onSubmit, onCancel }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Checklist>>({
    name: '',
    description: '',
    variables: [],
    items: [],
    templateId: '',
    locationIds: []
  });
  const [newItemText, setNewItemText] = useState('');
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState<Record<number, string>>({});
  const [expandedVariables, setExpandedVariables] = useState<Set<number>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [isLocationSpecific, setIsLocationSpecific] = useState(false);
  const [copyFromId, setCopyFromId] = useState<string>('');
  // State for new evidence definition inputs per item
  const [newEvidenceName, setNewEvidenceName] = useState<Record<number, string>>({});
  const [newEvidenceRequired, setNewEvidenceRequired] = useState<Record<number, boolean>>({});
  const [newEvidenceDescription, setNewEvidenceDescription] = useState<Record<number, string>>({});
  const [newEvidenceWorkflowRules, setNewEvidenceWorkflowRules] = useState<Record<number, string>>({});
  
  // State for field definitions management
  const [expandedEvidenceDefs, setExpandedEvidenceDefs] = useState<Set<string>>(new Set()); // "itemIndex:evidenceIndex"
  const [editingField, setEditingField] = useState<string | null>(null); // "itemIndex:evidenceIndex:fieldIndex" or "itemIndex:evidenceIndex:new"
  const [newFieldData, setNewFieldData] = useState<Record<string, NewFieldDraft>>({});
  
  // State for split window editing
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null); // Track which item is selected to show evidence in right panel
  const [addingFieldToEvidence, setAddingFieldToEvidence] = useState<{
    itemIndex: number;
    evidenceIndex: number;
  } | null>(null); // Track which evidence is selected to add fields
  const [editingEvidence, setEditingEvidence] = useState<{
    itemIndex: number;
    evidenceIndex: number;
  } | null>(null);
  const [editingFieldDetail, setEditingFieldDetail] = useState<{
    itemIndex: number;
    evidenceIndex: number;
    fieldIndex: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'items'>('config');

  useEffect(() => {
    loadTemplates();
    loadLocations();
    loadAssetTypes();
    if (checklist) {
      // Convert legacy string[] variables to ChecklistVariable[] if needed
      let variables: ChecklistVariable[] = [];
      if (checklist.variables) {
        if (Array.isArray(checklist.variables) && checklist.variables.length > 0) {
          // Check if first item is a string (legacy format)
          const firstItem = checklist.variables[0];
          if (typeof firstItem === 'string') {
            // Legacy format: convert string[] to ChecklistVariable[]
            variables = (checklist.variables as unknown as string[]).map(name => ({
              name,
              possibleValues: []
            }));
          } else {
            // New format: ChecklistVariable[] — ensure each variable has possibleValues
            variables = (checklist.variables as ChecklistVariable[]).map(v => ({
              ...v,
              possibleValues: Array.isArray(v.possibleValues) ? v.possibleValues : []
            }));
          }
        }
      }
      
      const locationIds = checklist.locationIds || [];
      setIsLocationSpecific(locationIds.length > 0);
      
      // Migrate old checklists: convert categoryCode to evidenceName
      const migratedItems = (checklist.items || []).map(item => {
        if (item.evidenceDefinitions) {
          return {
            ...item,
            evidenceDefinitions: item.evidenceDefinitions.map(evDef => {
              // If it has categoryCode but no evidenceName, migrate it
              if ((evDef as any).categoryCode && !evDef.evidenceName) {
                return {
                  ...evDef,
                  evidenceName: (evDef as any).categoryCode, // Use categoryCode as evidenceName
                  categoryCode: undefined // Remove old field
                };
              }
              return evDef;
            })
          };
        }
        return item;
      });
      
      setFormData({
        name: checklist.name,
        description: checklist.description,
        variables: variables,
        items: migratedItems,
        templateId: checklist.templateId || '',
        locationIds: locationIds
      });
    }
  }, [checklist]);

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
  };

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    // Fetch user's full data from Firestore to get locationIds and role
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    try {
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadAssetTypes = async () => {
    if (!currentOrganization?.id) return;
    const result = await assetService.getAllAssetTypes(currentOrganization.id);
    if (result.success && result.data) {
      setAssetTypes(result.data);
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setFormData(prev => {
      const currentIds = prev.locationIds || [];
      const newIds = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: newIds };
    });
  };

  const handleLocationSpecificToggle = (checked: boolean) => {
    setIsLocationSpecific(checked);
    if (!checked) {
      // Clear locationIds when toggle is off
      setFormData(prev => ({ ...prev, locationIds: [] }));
    }
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  const handleExportChecklistJson = () => {
    const template = templates.find(t => t.id === formData.templateId);
    const payload = {
      exportVersion: 1 as const,
      exportedAt: new Date().toISOString(),
      sourceChecklistId: checklist?.id,
      checklist: {
        name: formData.name || '',
        description: formData.description,
        variables: formData.variables || [],
        items: formData.items || [],
        templateId: formData.templateId,
        locationIds: formData.locationIds || [],
        assetTypeIds: formData.assetTypeIds,
      },
      complianceTemplateName: template?.name,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const raw = (formData.name || 'checklist').trim() || 'checklist';
    const safeName = raw.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '-').slice(0, 80) || 'checklist';
    a.download = `${safeName}-checklist.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please enter a checklist name');
      return;
    }
    if (!formData.templateId) {
      alert('Please select a compliance template');
      return;
    }
    if (!formData.items || formData.items.length === 0) {
      alert('Please add at least one checklist item');
      return;
    }
    if (isLocationSpecific && (!formData.locationIds || formData.locationIds.length === 0)) {
      alert('Please select at least one location');
      return;
    }
    
    // Build submit data - only include locationIds if location-specific is enabled
    const submitData: Partial<Checklist> = {
      ...formData
    };
    
    // Clean up legacy categoryCode fields and ensure evidenceName is used
    // Also ensure all nested field definition properties are preserved
    if (submitData.items) {
      // Debug: Log field definitions before processing
      console.log('[ChecklistForm] Processing items for submit:', JSON.stringify(submitData.items, null, 2));
      submitData.items = submitData.items.map(item => {
        if (item.evidenceDefinitions) {
          return {
            ...item,
            evidenceDefinitions: item.evidenceDefinitions.map(evDef => {
              const cleaned: any = {
                evidenceName: evDef.evidenceName || (evDef as any).categoryCode || 'Unknown Evidence',
                required: evDef.required,
              };
              
              // Only include optional properties if they have values (don't set to undefined)
              if (evDef.description) cleaned.description = evDef.description;
              if (evDef.evidenceType) cleaned.evidenceType = evDef.evidenceType;
              if (evDef.source) cleaned.source = evDef.source;
              if (evDef.workflowRules) cleaned.workflowRules = evDef.workflowRules;
              
              // Deep copy fieldDefinitions to ensure all nested properties are preserved
              cleaned.fieldDefinitions = evDef.fieldDefinitions?.map(fieldDef => {
                  // Build the field definition object, preserving all properties
                  const field: any = {
                    key: fieldDef.key,
                    label: fieldDef.label,
                    type: fieldDef.type,
                    required: fieldDef.required
                  };
                  
                  // Only include optional properties if they have values
                  // Handle fileNameKey/fileNameKeys (backward compatibility)
                  if (fieldDef.fileNameKeys && fieldDef.fileNameKeys.length > 0) {
                    field.fileNameKeys = fieldDef.fileNameKeys;
                  } else if (fieldDef.fileNameKey) {
                    field.fileNameKey = fieldDef.fileNameKey;
                  }
                  if (fieldDef.workflowRules) field.workflowRules = fieldDef.workflowRules;
                  if (fieldDef.enumValues && fieldDef.enumValues.length > 0) field.enumValues = fieldDef.enumValues;
                  
                  // Deep copy extractionHints - preserve all properties including regex
                  if (fieldDef.extractionHints) {
                    const hints: any = {};
                    if (fieldDef.extractionHints.keywords && fieldDef.extractionHints.keywords.length > 0) {
                      hints.keywords = fieldDef.extractionHints.keywords;
                    }
                    if (fieldDef.extractionHints.anchors && fieldDef.extractionHints.anchors.length > 0) {
                      hints.anchors = fieldDef.extractionHints.anchors;
                    }
                    if (fieldDef.extractionHints.regex && typeof fieldDef.extractionHints.regex === 'string' && fieldDef.extractionHints.regex.trim().length > 0) {
                      hints.regex = fieldDef.extractionHints.regex.trim();
                    }
                    // Only include extractionHints if it has at least one property
                    if (Object.keys(hints).length > 0) {
                      field.extractionHints = hints;
                    }
                  }
                  
                  // Deep copy normalizationRules - preserve all properties including boolean false
                  if (fieldDef.normalizationRules) {
                    const normRules: any = {};
                    if (fieldDef.normalizationRules.dateFormat) normRules.dateFormat = fieldDef.normalizationRules.dateFormat;
                    if (fieldDef.normalizationRules.trim !== undefined) normRules.trim = fieldDef.normalizationRules.trim;
                    if (fieldDef.normalizationRules.uppercase !== undefined) normRules.uppercase = fieldDef.normalizationRules.uppercase;
                    if (fieldDef.normalizationRules.lowercase !== undefined) normRules.lowercase = fieldDef.normalizationRules.lowercase;
                    // Only include normalizationRules if it has at least one property
                    if (Object.keys(normRules).length > 0) {
                      field.normalizationRules = normRules;
                    }
                  }
                  
                  // Deep copy validationRules - preserve all properties
                  if (fieldDef.validationRules) {
                    const valRules: any = {};
                    if (fieldDef.validationRules.min !== undefined) valRules.min = fieldDef.validationRules.min;
                    if (fieldDef.validationRules.max !== undefined) valRules.max = fieldDef.validationRules.max;
                    if (fieldDef.validationRules.minLength !== undefined) valRules.minLength = fieldDef.validationRules.minLength;
                    if (fieldDef.validationRules.maxLength !== undefined) valRules.maxLength = fieldDef.validationRules.maxLength;
                    if (fieldDef.validationRules.pattern) valRules.pattern = fieldDef.validationRules.pattern;
                    if (fieldDef.validationRules.mustBeLessThan) valRules.mustBeLessThan = fieldDef.validationRules.mustBeLessThan;
                    if (fieldDef.validationRules.mustBeGreaterThan) valRules.mustBeGreaterThan = fieldDef.validationRules.mustBeGreaterThan;
                    if (fieldDef.validationRules.customValidation) valRules.customValidation = fieldDef.validationRules.customValidation;
                    // Only include validationRules if it has at least one property
                    if (Object.keys(valRules).length > 0) {
                      field.validationRules = valRules;
                    }
                  }
                  
                  return field;
                });
              
              // Explicitly remove categoryCode if it exists (legacy field)
              if ((evDef as any).categoryCode) {
                delete (cleaned as any).categoryCode;
              }
              return cleaned;
            })
          };
        }
        return item;
      });
    }
    
    // If location-specific is enabled, include locationIds; otherwise remove it from the object
    if (isLocationSpecific && formData.locationIds && formData.locationIds.length > 0) {
      submitData.locationIds = formData.locationIds;
    } else {
      // Remove locationIds from the object (don't set to undefined - Firestore doesn't accept undefined)
      delete submitData.locationIds;
    }
    
    onSubmit(submitData);
  };

  const addItem = () => {
    if (!newItemText.trim()) return;

    const newItem: ChecklistItem = {
      text: newItemText.trim(),
      order: (formData.items?.length || 0) + 1
    };

    setFormData({
      ...formData,
      items: [...(formData.items || []), newItem]
    });
    setNewItemText('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addItem();
    }
  };

  const removeItem = (index: number) => {
    const updatedItems = formData.items?.filter((_, i) => i !== index) || [];
    // Reorder items
    updatedItems.forEach((item, i) => {
      item.order = i + 1;
    });
    setFormData({
      ...formData,
      items: updatedItems
    });
    // Remove from expanded set
    const newExpanded = new Set(expandedItems);
    newExpanded.delete(index);
    setExpandedItems(newExpanded);
    // Clean up state for removed item
    delete newEvidenceName[index];
    delete newEvidenceRequired[index];
    delete newEvidenceDescription[index];
    delete newEvidenceWorkflowRules[index];
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (!formData.items) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.items.length) return;

    const updatedItems = [...formData.items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    
    // Update order
    updatedItems.forEach((item, i) => {
      item.order = i + 1;
    });
    
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const addVariable = () => {
    if (!newVariableName.trim()) return;
    const newVar: ChecklistVariable = {
      name: newVariableName.trim(),
      possibleValues: []
    };
    setFormData({
      ...formData,
      variables: [...(formData.variables || []), newVar]
    });
    setNewVariableName('');
    // Auto-expand the newly added variable
    const newIndex = (formData.variables?.length || 0);
    setExpandedVariables(new Set([...expandedVariables, newIndex]));
  };

  const removeVariable = (index: number) => {
    const updatedVariables = formData.variables?.filter((_, i) => i !== index) || [];
    setFormData({
      ...formData,
      variables: updatedVariables
    });
    // Remove from expanded set
    const newExpanded = new Set(expandedVariables);
    newExpanded.delete(index);
    setExpandedVariables(newExpanded);
  };

  const toggleVariableExpanded = (index: number) => {
    const newExpanded = new Set(expandedVariables);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedVariables(newExpanded);
  };

  const addVariableValue = (varIndex: number) => {
    const valueToAdd = newVariableValue[varIndex]?.trim();
    if (!valueToAdd) return;

    const updatedVariables = [...(formData.variables || [])];
    if (updatedVariables[varIndex]) {
      updatedVariables[varIndex] = {
        ...updatedVariables[varIndex],
        possibleValues: [...(updatedVariables[varIndex].possibleValues ?? []), valueToAdd]
      };
      setFormData({
        ...formData,
        variables: updatedVariables
      });
      setNewVariableValue({ ...newVariableValue, [varIndex]: '' });
    }
  };

  const removeVariableValue = (varIndex: number, valueIndex: number) => {
    const updatedVariables = [...(formData.variables || [])];
    if (updatedVariables[varIndex]) {
      updatedVariables[varIndex] = {
        ...updatedVariables[varIndex],
        possibleValues: (updatedVariables[varIndex].possibleValues ?? []).filter((_, i) => i !== valueIndex)
      };
      setFormData({
        ...formData,
        variables: updatedVariables
      });
    }
  };

  const handleVariableKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addVariable();
    }
  };

  // Evidence Definitions Management
  const toggleItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const addEvidenceDefinition = (itemIndex: number) => {
    const evidenceName = newEvidenceName[itemIndex]?.trim();
    if (!evidenceName) return;

    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex]) return;

    const newEvidenceDef: ChecklistItemEvidenceDefinition = {
      evidenceName,
      required: newEvidenceRequired[itemIndex] ?? false,
      // evidenceType and source will be auto-populated by backend
      description: newEvidenceDescription[itemIndex]?.trim() || undefined,
      workflowRules: newEvidenceWorkflowRules[itemIndex]?.trim() || undefined,
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: [...(updatedItems[itemIndex].evidenceDefinitions || []), newEvidenceDef]
    };

    setFormData({
      ...formData,
      items: updatedItems
    });

    // Clear input fields for this item
    setNewEvidenceName({ ...newEvidenceName, [itemIndex]: '' });
    setNewEvidenceRequired({ ...newEvidenceRequired, [itemIndex]: false });
    setNewEvidenceDescription({ ...newEvidenceDescription, [itemIndex]: '' });
    setNewEvidenceWorkflowRules({ ...newEvidenceWorkflowRules, [itemIndex]: '' });
  };

  const removeEvidenceDefinition = (itemIndex: number, evidenceIndex: number) => {
    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: updatedItems[itemIndex].evidenceDefinitions!.filter((_, i) => i !== evidenceIndex)
    };

    setFormData({
      ...formData,
      items: updatedItems
    });

    // Clean up expanded state and field data
    const evidenceKey = getEvidenceDefKey(itemIndex, evidenceIndex);
    const newExpanded = new Set(expandedEvidenceDefs);
    newExpanded.delete(evidenceKey);
    setExpandedEvidenceDefs(newExpanded);

    // Remove all field data for this evidence definition
    const newFieldDataCopy = { ...newFieldData };
    Object.keys(newFieldDataCopy).forEach(key => {
      if (key.startsWith(`${itemIndex}:${evidenceIndex}:`)) {
        delete newFieldDataCopy[key];
      }
    });
    setNewFieldData(newFieldDataCopy);
  };

  const updateEvidenceDefinition = (
    itemIndex: number,
    evidenceIndex: number,
    updates: Partial<ChecklistItemEvidenceDefinition>
  ) => {
    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    evidenceDefs[evidenceIndex] = { ...evidenceDefs[evidenceIndex], ...updates };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  // Field Definitions Management
  const getEvidenceDefKey = (itemIndex: number, evidenceIndex: number) => `${itemIndex}:${evidenceIndex}`;
  const getFieldKey = (itemIndex: number, evidenceIndex: number, fieldIndex: number | 'new') => `${itemIndex}:${evidenceIndex}:${fieldIndex}`;

  const toggleEvidenceDefExpanded = (itemIndex: number, evidenceIndex: number) => {
    const key = getEvidenceDefKey(itemIndex, evidenceIndex);
    const newExpanded = new Set(expandedEvidenceDefs);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedEvidenceDefs(newExpanded);
  };

  const addFieldDefinition = (itemIndex: number, evidenceIndex: number) => {
    const fieldKey = getFieldKey(itemIndex, evidenceIndex, 'new');
    const fieldData = newFieldData[fieldKey];
    
    if (!fieldData || !fieldData.key || !fieldData.label || !fieldData.type) {
      return; // Validation
    }

    // Handle fileNameKeys: parse from string (raw input) or array; then store as fileNameKeys array or fileNameKey (backward compat)
    const toFileNameKeysArray = (v: string | string[] | undefined): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.filter(k => k && k.trim().length > 0).map(k => k.trim());
      return String(v).split(',').map(k => k.trim()).filter(Boolean);
    };
    const fileNameKeysArr = toFileNameKeysArray(fieldData.fileNameKeys);
    const fileNameKey = fieldData.fileNameKey?.trim() || undefined;
    const fileNameKeys = fileNameKeysArr.length > 0 ? fileNameKeysArr : (fileNameKey ? [fileNameKey] : []);
    const toEnumValuesArray = (v: string | string[] | undefined): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.filter(Boolean).map(k => k.trim()).filter(Boolean);
      return String(v).split(',').map(k => k.trim()).filter(Boolean);
    };
    const enumValuesArr = fieldData.type === 'enum' ? toEnumValuesArray(fieldData.enumValues) : [];
    const toKeywordsArray = (v: string | string[] | undefined): string[] | undefined => {
      if (v == null) return undefined;
      if (Array.isArray(v)) return v.length > 0 ? v : undefined;
      const arr = String(v).split(',').map((s: string) => s.trim()).filter(Boolean);
      return arr.length > 0 ? arr : undefined;
    };
    const eh = fieldData.extractionHints;
    const ehKeywords = eh ? toKeywordsArray(eh.keywords) : undefined;
    const ehAnchors = eh ? toKeywordsArray(eh.anchors) : undefined;
    const ehRegex = eh?.regex?.trim();
    const extractionHints = (ehKeywords || ehAnchors || ehRegex) ? { ...(ehKeywords && { keywords: ehKeywords }), ...(ehAnchors && { anchors: ehAnchors }), ...(ehRegex && { regex: ehRegex }) } : undefined;

    const newField: EvidenceFieldDefinition = {
      key: fieldData.key.trim(),
      label: fieldData.label.trim(),
      type: fieldData.type,
      required: fieldData.required ?? false,
      // Store as fileNameKeys if multiple, or fileNameKey if single (for backward compatibility)
      fileNameKeys: fileNameKeys.length > 1 ? fileNameKeys : undefined,
      fileNameKey: fileNameKeys.length === 1 ? fileNameKeys[0] : (fileNameKeys.length === 0 ? fileNameKey : fileNameKeys[0]),
      enumValues: fieldData.type === 'enum' && enumValuesArr.length > 0 ? enumValuesArr : undefined,
      extractionHints,
      normalizationRules: fieldData.normalizationRules,
      validationRules: fieldData.validationRules,
      workflowRules: fieldData.workflowRules?.trim() || undefined,
    };

    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    evidenceDefs[evidenceIndex] = {
      ...evidenceDefs[evidenceIndex],
      fieldDefinitions: [...(evidenceDefs[evidenceIndex].fieldDefinitions || []), newField]
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setFormData({
      ...formData,
      items: updatedItems
    });

    // Clear new field data
    const newFieldDataCopy = { ...newFieldData };
    delete newFieldDataCopy[fieldKey];
    setNewFieldData(newFieldDataCopy);
    setEditingField(null);
  };

  const updateFieldDefinition = (itemIndex: number, evidenceIndex: number, fieldIndex: number, updates: Partial<EvidenceFieldDefinition>) => {
    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    const fieldDefs = [...(evidenceDefs[evidenceIndex].fieldDefinitions || [])];
    fieldDefs[fieldIndex] = { ...fieldDefs[fieldIndex], ...updates };

    evidenceDefs[evidenceIndex] = {
      ...evidenceDefs[evidenceIndex],
      fieldDefinitions: fieldDefs
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const removeFieldDefinition = (itemIndex: number, evidenceIndex: number, fieldIndex: number) => {
    const updatedItems = [...(formData.items || [])];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    const fieldDefs = (evidenceDefs[evidenceIndex].fieldDefinitions || []).filter((_, i) => i !== fieldIndex);

    evidenceDefs[evidenceIndex] = {
      ...evidenceDefs[evidenceIndex],
      fieldDefinitions: fieldDefs.length > 0 ? fieldDefs : undefined
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[120] flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl w-[95vw] h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {checklist ? 'Edit Checklist' : 'Add New Checklist'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'config'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Config
          </button>
          <button
            onClick={() => setActiveTab('items')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'items'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Items
          </button>
        </div>

        {/* Split Window Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Checklist Editor (full height) */}
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200">
            {/* Form Content - fills height so Items tab can use full left side */}
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto p-4">
            <form id="checklist-form" onSubmit={handleSubmit} className={`flex flex-col flex-1 min-h-0 overflow-hidden ${activeTab === 'config' ? 'space-y-3' : ''}`}>
            {/* Config Tab Content */}
            {activeTab === 'config' && (
              <>
            {/* Copy From (only show when adding new checklist) */}
            {!checklist && checklists.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Copy From (Optional)
                </label>
                <select
                  value={copyFromId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setCopyFromId(selectedId);
                    if (selectedId) {
                      const sourceChecklist = checklists.find(c => c.id === selectedId);
                      if (sourceChecklist) {
                        // Convert variables if needed
                        let variables: ChecklistVariable[] = [];
                        if (sourceChecklist.variables) {
                          if (Array.isArray(sourceChecklist.variables) && sourceChecklist.variables.length > 0) {
                            const firstItem = sourceChecklist.variables[0];
                            if (typeof firstItem === 'string') {
                              variables = (sourceChecklist.variables as unknown as string[]).map(name => ({
                                name,
                                possibleValues: []
                              }));
                            } else {
                              variables = sourceChecklist.variables as ChecklistVariable[];
                            }
                          }
                        }
                        
                        const locationIds = sourceChecklist.locationIds || [];
                        setIsLocationSpecific(locationIds.length > 0);
                        
                        setFormData({
                          name: '', // Clear name so user enters new one
                          description: sourceChecklist.description || '',
                          variables: variables,
                          items: sourceChecklist.items ? [...sourceChecklist.items] : [],
                          templateId: sourceChecklist.templateId || '',
                          locationIds: locationIds
                        });
                      }
                    } else {
                      // Reset form when "None" is selected
                      setFormData({
                        name: '',
                        description: '',
                        variables: [],
                        items: [],
                        templateId: '',
                        locationIds: []
                      });
                      setIsLocationSpecific(false);
                    }
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">None - Create New</option>
                  {checklists.map(cl => (
                    <option key={cl.id} value={cl.id}>{cl.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Row 1: Name and Template */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Checklist name"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Compliance <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.templateId || ''}
                  onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select...</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Note: To manage asset types linked to this checklist, go to the <strong>Assets</strong> menu item.
                </p>
              </div>
            </div>

            {/* Asset Types (Read-only display when editing) */}
            {checklist && checklist.templateId && (() => {
              // Find asset types linked to this checklist's compliance template
              const linkedAssetTypes = assetTypes.filter(
                at => at.complianceTemplateIds?.includes(checklist.templateId!)
              );
              
              return linkedAssetTypes.length > 0 ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Asset Types
                  </label>
                  <div className="flex flex-wrap gap-1 p-2 border border-gray-300 rounded-md bg-gray-50">
                    {linkedAssetTypes.map((assetType) => (
                      <span key={assetType.id} className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-medium">
                        {assetType.name}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Asset types are linked via the compliance template. To change asset types, update the asset type's compliance template assignment in the <strong>Assets</strong> menu item.
                  </p>
                </div>
              ) : null;
            })()}

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </div>

            {/* Location Restriction */}
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="location-specific"
                  checked={isLocationSpecific}
                  onChange={(e) => handleLocationSpecificToggle(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="location-specific" className="text-xs font-medium text-gray-700">
                  Available only to specific locations
                </label>
              </div>
              {isLocationSpecific && (
                <div className="mt-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={locationSearchTerm}
                      onChange={(e) => setLocationSearchTerm(e.target.value)}
                      placeholder="Search locations..."
                      className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 bg-white">
                    {filteredLocations.length === 0 ? (
                      <div className="text-xs text-gray-500 text-center py-2">
                        {locationSearchTerm ? 'No locations found' : 'No locations available'}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredLocations.map((location) => (
                          <label
                            key={location.id}
                            className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={(formData.locationIds || []).includes(location.id || '')}
                              onChange={() => location.id && handleLocationToggle(location.id)}
                              className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-xs text-gray-700">{location.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {(formData.locationIds || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {locations
                        .filter(loc => (formData.locationIds || []).includes(loc.id || ''))
                        .map((location) => (
                          <span
                            key={location.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 rounded text-xs"
                          >
                            {location.name}
                            <button
                              type="button"
                              onClick={() => location.id && handleLocationToggle(location.id)}
                              className="hover:text-primary-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Variables */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Variables (Optional) - Add possible values for each variable
              </label>
              {/* Add New Variable */}
              <div className="flex gap-1.5 mb-1.5">
                <input
                  type="text"
                  value={newVariableName}
                  onChange={(e) => setNewVariableName(e.target.value)}
                  onKeyPress={handleVariableKeyPress}
                  placeholder="e.g., Room Number"
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={addVariable}
                  className="btn-primary px-2 py-1.5 text-sm"
                  disabled={!newVariableName.trim()}
                >
                  Add
                </button>
              </div>

              {/* Variables List */}
              {formData.variables && formData.variables.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {formData.variables.map((variable, index) => {
                    const possibleValues = variable.possibleValues ?? [];
                    return (
                    <div key={index} className="bg-gray-50 rounded p-2 border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 flex-1">
                          <button
                            type="button"
                            onClick={() => toggleVariableExpanded(index)}
                            className="text-xs text-gray-600 hover:text-gray-800"
                            title={expandedVariables.has(index) ? "Collapse" : "Expand to add values"}
                          >
                            {expandedVariables.has(index) ? '▼' : '▶'} {variable.name}
                          </button>
                          {possibleValues.length > 0 && (
                            <span className="text-xs text-gray-500">
                              ({possibleValues.length} value{possibleValues.length !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      <button
                        type="button"
                        onClick={() => removeVariable(index)}
                          className="text-red-600 hover:text-red-800 text-xs"
                          title="Remove variable"
                        >
                          ×
                        </button>
                      </div>

                      {expandedVariables.has(index) && (
                        <div className="mt-2 space-y-1.5 pl-4 border-l-2 border-gray-300">
                          {/* Add Value Input */}
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={newVariableValue[index] || ''}
                              onChange={(e) => setNewVariableValue({ ...newVariableValue, [index]: e.target.value })}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  addVariableValue(index);
                                }
                              }}
                              placeholder={`Add value for ${variable.name}...`}
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                            <button
                              type="button"
                              onClick={() => addVariableValue(index)}
                              className="btn-primary px-2 py-1 text-xs"
                              disabled={!newVariableValue[index]?.trim()}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>

                          {/* Values List */}
                          {possibleValues.length > 0 && (
                            <div className="space-y-1">
                              {possibleValues.map((value, valueIndex) => (
                                <div key={valueIndex} className="flex items-center justify-between bg-white px-2 py-1 rounded text-xs border border-gray-200">
                                  <span className="text-gray-700">{value}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeVariableValue(index, valueIndex)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            {/* Link to Items Tab */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    Ready to add checklist items?
                  </p>
                  <p className="text-xs text-blue-700">
                    Switch to the Items tab to add, edit, and configure checklist items and their evidence fields.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('items')}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  Update Checklist Items →
                </button>
              </div>
            </div>
              </>
            )}

            {/* Items Tab Content - fills full left panel height */}
            {activeTab === 'items' && (
              <div className="flex flex-col flex-1 min-h-0">
            {/* Checklist Items */}
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between gap-2 mb-1 flex-shrink-0">
                <label className="block text-xs font-medium text-gray-700">Checklist Items *</label>
                <button
                  type="button"
                  onClick={handleExportChecklistJson}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
                  title="Download this checklist definition as JSON (items, variables, evidence fields) for backup or another organization"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>
              </div>
              
              {/* Add New Item */}
              <div className="flex gap-1.5 mb-1.5 flex-shrink-0">
                <input
                  type="text"
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter checklist item"
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={addItem}
                  className="btn-primary px-2 py-1.5 text-sm"
                >
                  Add
                </button>
              </div>

              {/* Items List - grows to fill remaining height */}
              <div className="flex-1 min-h-0 space-y-2 overflow-y-auto border border-gray-300 rounded-md p-2">
                {formData.items?.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded border border-gray-200">
                    {/* Item Header */}
                    <div className="flex items-center gap-1.5 p-1.5">
                      <button
                        type="button"
                        onClick={() => toggleItemExpanded(index)}
                        className="text-gray-600 hover:text-gray-800"
                        title={expandedItems.has(index) ? "Collapse evidence definitions" : "Expand to add evidence definitions"}
                      >
                        {expandedItems.has(index) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <span className="font-medium text-gray-500 w-4">{index + 1}.</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemIndex(selectedItemIndex === index ? null : index);
                          setEditingEvidence(null);
                          setEditingFieldDetail(null);
                        }}
                        className="flex-1 text-left text-gray-700 text-xs hover:text-blue-600"
                        title="Click to add evidence definition"
                      >
                        {item.text}
                      </button>
                      {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                        <span className="text-xs text-gray-500 bg-blue-100 px-1.5 py-0.5 rounded">
                          {item.evidenceDefinitions.length} evidence{item.evidenceDefinitions.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <div className="flex gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveItem(index, 'up');
                          }}
                          disabled={index === 0}
                          className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveItem(index, 'down');
                          }}
                          disabled={index === (formData.items?.length || 0) - 1}
                          className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(index);
                            if (selectedItemIndex === index) {
                              setSelectedItemIndex(null);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 px-1"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    {/* Evidence Definitions Section */}
                    {expandedItems.has(index) && (
                      <div className="pl-6 pr-2 pb-2 space-y-2 border-t border-gray-200 bg-white">
                        <div className="text-xs font-medium text-gray-700 pt-2">
                          Evidence Definitions (Optional)
                        </div>
                        
                        {/* Existing Evidence Definitions */}
                        {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                          <div className="space-y-2">
                            {item.evidenceDefinitions.map((evidenceDef, evidenceIndex) => {
                              const evidenceKey = getEvidenceDefKey(index, evidenceIndex);
                              const isExpanded = expandedEvidenceDefs.has(evidenceKey);
                              const fieldCount = evidenceDef.fieldDefinitions?.length || 0;
                              
                              return (
                                <div key={evidenceIndex} className="bg-white border border-gray-200 rounded text-xs">
                                  {/* Evidence Definition Header */}
                                  <div className="flex items-start gap-2 p-2">
                                    <button
                                      type="button"
                                      onClick={() => toggleEvidenceDefExpanded(index, evidenceIndex)}
                                      className="text-gray-600 hover:text-gray-800"
                                      title={isExpanded ? "Collapse fields" : "Expand to manage fields"}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="w-4 h-4" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4" />
                                      )}
                                    </button>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-1 mb-1">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setAddingFieldToEvidence({ itemIndex: index, evidenceIndex });
                                            setEditingEvidence(null);
                                            setEditingFieldDetail(null);
                                            setSelectedItemIndex(null);
                                          }}
                                          className="font-medium text-gray-700 hover:text-blue-600 cursor-pointer"
                                          title="Click to add field to this evidence"
                                        >
                                          {evidenceDef.evidenceName || 'Unknown Evidence'}
                                        </button>
                                        {evidenceDef.required && (
                                          <span className="bg-red-100 text-red-700 px-1 rounded text-xs">Required</span>
                                        )}
                                        {!evidenceDef.required && (
                                          <span className="bg-gray-100 text-gray-600 px-1 rounded text-xs">Optional</span>
                                        )}
                                        {fieldCount > 0 && (
                                          <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs">
                                            {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                      </div>
                                      {evidenceDef.description && (
                                        <div className="text-gray-600 italic mt-1">
                                          {evidenceDef.description}
                                        </div>
                                      )}
                                      {evidenceDef.workflowRules && (
                                        <div className="text-blue-600 text-xs mt-1 bg-blue-50 p-1 rounded border border-blue-200">
                                          <strong>Workflow Rules:</strong> {evidenceDef.workflowRules}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingFieldDetail(null);
                                          requestAnimationFrame(() => {
                                            setEditingEvidence({ itemIndex: index, evidenceIndex });
                                          });
                                        }}
                                        className="text-blue-600 hover:text-blue-800 p-1"
                                        title="Edit evidence definition"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeEvidenceDefinition(index, evidenceIndex)}
                                        className="text-red-600 hover:text-red-800 p-1"
                                        title="Remove evidence definition"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Field Definitions Section */}
                                  {isExpanded && (
                                    <div className="pl-6 pr-2 pb-2 space-y-2 border-t border-gray-200 bg-gray-50">
                                      <div className="text-xs font-medium text-gray-700 pt-2">
                                        Field Definitions (Optional)
                                      </div>

                                      {/* Existing Fields */}
                                      {evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0 && (
                                        <div className="space-y-1.5">
                                          {evidenceDef.fieldDefinitions.map((field, fieldIndex) => (
                                            <div key={fieldIndex} className="p-2 bg-white border border-gray-200 rounded text-xs">
                                              <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-1 flex-wrap">
                                                  <span className="font-medium text-gray-700">{field.label}</span>
                                                  <span className="text-gray-500">({field.key})</span>
                                                  <span className="text-gray-500 text-xs">- {field.type}</span>
                                                  {field.required && (
                                                    <span className="bg-red-100 text-red-700 px-1 rounded text-xs">Required</span>
                                                  )}
                                                  {(() => {
                                                    // Show fileNameKeys (new format) or fileNameKey (legacy)
                                                    const fileNameKeys = field.fileNameKeys && field.fileNameKeys.length > 0 
                                                      ? field.fileNameKeys 
                                                      : (field.fileNameKey ? [field.fileNameKey] : []);
                                                    return fileNameKeys.length > 0 && (
                                                      <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs" title={`Only processes files with these keys in filename: ${fileNameKeys.join(', ')}`}>
                                                        File: {fileNameKeys.join(', ')}
                                                      </span>
                                                    );
                                                  })()}
                                                </div>
                                                <div className="flex gap-1">
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setEditingEvidence(null);
                                                      requestAnimationFrame(() => {
                                                        setEditingFieldDetail({ itemIndex: index, evidenceIndex, fieldIndex });
                                                      });
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 p-1"
                                                    title="Edit field"
                                                  >
                                                    <Edit2 className="w-3 h-3" />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => removeFieldDefinition(index, evidenceIndex, fieldIndex)}
                                                    className="text-red-600 hover:text-red-800 p-1"
                                                    title="Remove field"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              </div>
                                              {field.enumValues && field.enumValues.length > 0 && (
                                                <div className="text-gray-600 text-xs mt-1">
                                                  Values: {field.enumValues.join(', ')}
                                                </div>
                                              )}
                                              {field.workflowRules && (
                                                <div className="text-blue-600 text-xs mt-1 bg-blue-50 p-1 rounded border border-blue-200">
                                                  <strong>Workflow Rules:</strong> {field.workflowRules}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {(!formData.items || formData.items.length === 0) && (
                  <div className="text-center py-3 text-gray-500 text-xs">
                    No items added yet
                  </div>
                )}
              </div>
            </div>
              </div>
            )}
            </form>
            </div>
          </div>

          {/* Right Panel - Add Evidence Form / Edit Panels */}
          <div className="w-[500px] border-l border-gray-200 bg-gray-50 overflow-y-auto">
            {editingFieldDetail ? (
              <FieldEditPanel
                key={`field-${editingFieldDetail.itemIndex}-${editingFieldDetail.evidenceIndex}-${editingFieldDetail.fieldIndex}`}
                itemIndex={editingFieldDetail.itemIndex}
                evidenceIndex={editingFieldDetail.evidenceIndex}
                fieldIndex={editingFieldDetail.fieldIndex}
                field={formData.items?.[editingFieldDetail.itemIndex]?.evidenceDefinitions?.[editingFieldDetail.evidenceIndex]?.fieldDefinitions?.[editingFieldDetail.fieldIndex]}
                onSave={(updatedField) => {
                  const updatedItems = [...(formData.items || [])];
                  if (!updatedItems[editingFieldDetail.itemIndex] || 
                      !updatedItems[editingFieldDetail.itemIndex].evidenceDefinitions ||
                      !updatedItems[editingFieldDetail.itemIndex].evidenceDefinitions![editingFieldDetail.evidenceIndex] ||
                      !updatedItems[editingFieldDetail.itemIndex].evidenceDefinitions![editingFieldDetail.evidenceIndex].fieldDefinitions) return;
                  const fieldDefs = [...updatedItems[editingFieldDetail.itemIndex].evidenceDefinitions![editingFieldDetail.evidenceIndex].fieldDefinitions!];
                  fieldDefs[editingFieldDetail.fieldIndex] = updatedField;
                  updatedItems[editingFieldDetail.itemIndex] = {
                    ...updatedItems[editingFieldDetail.itemIndex],
                    evidenceDefinitions: updatedItems[editingFieldDetail.itemIndex].evidenceDefinitions!.map((evDef, evIdx) => 
                      evIdx === editingFieldDetail.evidenceIndex 
                        ? { ...evDef, fieldDefinitions: fieldDefs }
                        : evDef
                    )
                  };
                  setFormData({ ...formData, items: updatedItems });
                  setEditingFieldDetail(null);
                }}
                onCancel={() => setEditingFieldDetail(null)}
              />
            ) : editingEvidence ? (
              <EvidenceEditPanel
                key={`evidence-${editingEvidence.itemIndex}-${editingEvidence.evidenceIndex}`}
                itemIndex={editingEvidence.itemIndex}
                evidenceIndex={editingEvidence.evidenceIndex}
                evidence={formData.items?.[editingEvidence.itemIndex]?.evidenceDefinitions?.[editingEvidence.evidenceIndex]}
                onSave={(updatedEvidence) => {
                  const updatedItems = [...(formData.items || [])];
                  if (!updatedItems[editingEvidence.itemIndex] || !updatedItems[editingEvidence.itemIndex].evidenceDefinitions) return;
                  const evidenceDefs = [...updatedItems[editingEvidence.itemIndex].evidenceDefinitions!];
                  evidenceDefs[editingEvidence.evidenceIndex] = updatedEvidence;
                  updatedItems[editingEvidence.itemIndex] = {
                    ...updatedItems[editingEvidence.itemIndex],
                    evidenceDefinitions: evidenceDefs
                  };
                  setFormData({ ...formData, items: updatedItems });
                  setEditingEvidence(null);
                }}
                onCancel={() => setEditingEvidence(null)}
              />
            ) : addingFieldToEvidence && formData.items?.[addingFieldToEvidence.itemIndex]?.evidenceDefinitions?.[addingFieldToEvidence.evidenceIndex] ? (
              <div className="h-full flex flex-col">
                {/* Evidence Header in Right Panel */}
                <div className="p-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Add Field</h3>
                    <button
                      onClick={() => {
                        setAddingFieldToEvidence(null);
                        setEditingEvidence(null);
                        setEditingFieldDetail(null);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {formData.items[addingFieldToEvidence.itemIndex].evidenceDefinitions![addingFieldToEvidence.evidenceIndex].evidenceName}
                  </p>
                </div>

                {/* Add Field Form */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2 p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Key *
                        </label>
                        <input
                          type="text"
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.key || ''}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              key: e.target.value
                            }
                          })}
                          placeholder="e.g., recordId, evidenceDate"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Label *
                        </label>
                        <input
                          type="text"
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.label || ''}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              label: e.target.value
                            }
                          })}
                          placeholder="e.g., AFR Certificate ID"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Type *
                        </label>
                        <select
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type || ''}
                          onChange={(e) => {
                            const type = e.target.value as EvidenceFieldDefinition['type'];
                            setNewFieldData({
                              ...newFieldData,
                              [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                type: type,
                                enumValues: type === 'enum' ? [] : undefined
                              }
                            });
                          }}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        >
                          <option value="">Select type...</option>
                          <option value="string">String</option>
                          <option value="date">Date</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="enum">Enum</option>
                          <option value="list">List</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-1 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.required || false}
                            onChange={(e) => setNewFieldData({
                              ...newFieldData,
                              [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                required: e.target.checked
                              }
                            })}
                            className="w-3 h-3"
                          />
                          Required
                        </label>
                      </div>
                    </div>

                    {/* File Name Key(s) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        File Name Key(s) (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const fieldData = newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')];
                          // Show raw string while typing, or array/legacy as comma-separated
                          const fk = fieldData?.fileNameKeys;
                          if (typeof fk === 'string') return fk;
                          if (Array.isArray(fk) && fk.length > 0) return fk.join(', ');
                          return fieldData?.fileNameKey || '';
                        })()}
                        onChange={(e) => {
                          setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              fileNameKeys: e.target.value
                            }
                          });
                        }}
                        placeholder="e.g., loan-number, application-number (comma-separated for multiple keys)"
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enter one or more keys separated by commas. Files matching any key will be processed.
                      </p>
                    </div>

                    {/* Workflow Rules */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Workflow Rules (Optional)
                      </label>
                      <textarea
                        value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.workflowRules || ''}
                        onChange={(e) => setNewFieldData({
                          ...newFieldData,
                          [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                            ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                            workflowRules: e.target.value || undefined
                          }
                        })}
                        placeholder="e.g., if loanNumber AND borrowerName are present THEN yes ELSE no"
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        rows={2}
                      />
                    </div>

                    {/* Enum Values (if type is enum) */}
                    {newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type === 'enum' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Enum Values (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={(() => {
                            const ev = newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.enumValues;
                            return typeof ev === 'string' ? ev : (Array.isArray(ev) ? ev.join(', ') : '');
                          })()}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              enumValues: e.target.value
                            }
                          })}
                          placeholder="Value1, Value2, Value3"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    )}

                    {/* Extraction Hints */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Extraction Hints (Optional)
                      </label>
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={(() => {
                            const kw = newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints?.keywords;
                            return Array.isArray(kw) ? kw.join(', ') : (kw ?? '');
                          })()}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              extractionHints: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints,
                                keywords: e.target.value
                              }
                            }
                          })}
                          placeholder="Keywords (comma-separated, spaces allowed within a keyword)"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={(() => {
                            const an = newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints?.anchors;
                            return Array.isArray(an) ? an.join(', ') : (an ?? '');
                          })()}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              extractionHints: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints,
                                anchors: e.target.value
                              }
                            }
                          })}
                          placeholder="Anchor text (comma-separated, spaces allowed)"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints?.regex || ''}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              extractionHints: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.extractionHints,
                                regex: e.target.value || undefined
                              }
                            }
                          })}
                          placeholder="Regex pattern (optional)"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    {/* Normalization Rules */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Normalization (Optional)
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type === 'date' && (
                          <input
                            type="text"
                            value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.normalizationRules?.dateFormat || ''}
                            onChange={(e) => setNewFieldData({
                              ...newFieldData,
                              [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                normalizationRules: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.normalizationRules,
                                  dateFormat: e.target.value || undefined
                                }
                              }
                            })}
                            placeholder="Date format (e.g., YYYY-MM-DD)"
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        )}
                        <label className="flex items-center gap-1 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.normalizationRules?.trim || false}
                            onChange={(e) => setNewFieldData({
                              ...newFieldData,
                              [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                normalizationRules: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.normalizationRules,
                                  trim: e.target.checked || undefined
                                }
                              }
                            })}
                            className="w-3 h-3"
                          />
                          Trim whitespace
                        </label>
                      </div>
                    </div>

                    {/* Validation Rules */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Validation Rules (Optional)
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type === 'number' && (
                          <>
                            <input
                              type="number"
                              value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.min || ''}
                              onChange={(e) => setNewFieldData({
                                ...newFieldData,
                                [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                  validationRules: {
                                    ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                    min: e.target.value ? Number(e.target.value) : undefined
                                  }
                                }
                              })}
                              placeholder="Min value"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                            <input
                              type="number"
                              value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.max || ''}
                              onChange={(e) => setNewFieldData({
                                ...newFieldData,
                                [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                  validationRules: {
                                    ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                    max: e.target.value ? Number(e.target.value) : undefined
                                  }
                                }
                              })}
                              placeholder="Max value"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          </>
                        )}
                        {newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type === 'string' && (
                          <>
                            <input
                              type="number"
                              value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.minLength || ''}
                              onChange={(e) => setNewFieldData({
                                ...newFieldData,
                                [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                  validationRules: {
                                    ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                    minLength: e.target.value ? Number(e.target.value) : undefined
                                  }
                                }
                              })}
                              placeholder="Min length"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                            <input
                              type="number"
                              value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.maxLength || ''}
                              onChange={(e) => setNewFieldData({
                                ...newFieldData,
                                [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                                  ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                                  validationRules: {
                                    ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                    maxLength: e.target.value ? Number(e.target.value) : undefined
                                  }
                                }
                              })}
                              placeholder="Max length"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          </>
                        )}
                        <input
                          type="text"
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.mustBeLessThan || ''}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              validationRules: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                mustBeLessThan: e.target.value || undefined
                              }
                            }
                          })}
                          placeholder="Must be < field key"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules?.mustBeGreaterThan || ''}
                          onChange={(e) => setNewFieldData({
                            ...newFieldData,
                            [getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]: {
                              ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')],
                              validationRules: {
                                ...newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.validationRules,
                                mustBeGreaterThan: e.target.value || undefined
                              }
                            }
                          })}
                          placeholder="Must be > field key"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        addFieldDefinition(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex);
                        setAddingFieldToEvidence(null);
                      }}
                      disabled={!newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.key?.trim() || 
                               !newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.label?.trim() ||
                               !newFieldData[getFieldKey(addingFieldToEvidence.itemIndex, addingFieldToEvidence.evidenceIndex, 'new')]?.type}
                      className="btn-primary px-2 py-1 text-xs w-full"
                    >
                      <Plus className="w-3 h-3 inline mr-1" />
                      Add Field
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedItemIndex !== null && formData.items?.[selectedItemIndex] ? (
              <div className="h-full flex flex-col">
                {/* Item Header in Right Panel */}
                <div className="p-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Add Evidence Definition</h3>
                    <button
                      onClick={() => {
                        setSelectedItemIndex(null);
                        setEditingEvidence(null);
                        setEditingFieldDetail(null);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedItemIndex + 1}. {formData.items[selectedItemIndex].text}
                  </p>
                </div>

                {/* Add Evidence Definition Form */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2 p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Evidence Name *
                        </label>
                        <input
                          type="text"
                          value={newEvidenceName[selectedItemIndex] || ''}
                          onChange={(e) => setNewEvidenceName({ ...newEvidenceName, [selectedItemIndex]: e.target.value })}
                          placeholder="e.g., Joint Intent, Flood Determination"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-1 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={newEvidenceRequired[selectedItemIndex] || false}
                            onChange={(e) => setNewEvidenceRequired({ ...newEvidenceRequired, [selectedItemIndex]: e.target.checked })}
                            className="w-3 h-3"
                          />
                          Required
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Description (Optional)
                      </label>
                      <input
                        type="text"
                        value={newEvidenceDescription[selectedItemIndex] || ''}
                        onChange={(e) => setNewEvidenceDescription({ ...newEvidenceDescription, [selectedItemIndex]: e.target.value })}
                        placeholder="Helper text for this evidence requirement"
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">
                        Workflow Rules (Optional)
                      </label>
                      <textarea
                        value={newEvidenceWorkflowRules[selectedItemIndex] || ''}
                        onChange={(e) => setNewEvidenceWorkflowRules({ ...newEvidenceWorkflowRules, [selectedItemIndex]: e.target.value })}
                        placeholder="e.g., This evidence is required if property is in SFHA"
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-0.5">
                        💡 Tip: Describe when this evidence is required or optional based on asset facts
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => addEvidenceDefinition(selectedItemIndex)}
                      disabled={!newEvidenceName[selectedItemIndex]?.trim()}
                      className="btn-primary px-2 py-1 text-xs w-full"
                    >
                      <Plus className="w-3 h-3 inline mr-1" />
                      Add Evidence Definition
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer with buttons */}
        <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary px-4 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="checklist-form"
          className="btn-primary px-4 py-2 text-sm"
        >
          {checklist ? 'Update' : 'Create'} Checklist
        </button>
      </div>
    </div>
    </div>
  );
};

// Evidence Edit Panel Component
interface EvidenceEditPanelProps {
  itemIndex: number;
  evidenceIndex: number;
  evidence?: ChecklistItemEvidenceDefinition;
  onSave: (evidence: ChecklistItemEvidenceDefinition) => void;
  onCancel: () => void;
}

const EvidenceEditPanel: React.FC<EvidenceEditPanelProps> = ({ evidence, onSave, onCancel }) => {
  const [formData, setFormData] = useState<ChecklistItemEvidenceDefinition>({
    evidenceName: evidence?.evidenceName || '',
    required: evidence?.required ?? false,
    description: evidence?.description || '',
    workflowRules: evidence?.workflowRules || '',
    evidenceType: evidence?.evidenceType,
    source: evidence?.source,
    fieldDefinitions: evidence?.fieldDefinitions || []
  });

  const handleSave = () => {
    if (!formData.evidenceName.trim()) {
      alert('Evidence name is required');
      return;
    }
    onSave({
      ...formData,
      description: formData.description?.trim() || undefined,
      workflowRules: formData.workflowRules?.trim() || undefined,
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Evidence</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Evidence Name *
          </label>
          <input
            type="text"
            value={formData.evidenceName}
            onChange={(e) => setFormData({ ...formData, evidenceName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="e.g., FLOOD_INSURANCE"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={formData.required}
              onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
              className="w-4 h-4"
            />
            Required
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <input
            type="text"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Helper text for this evidence requirement"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Workflow Rules (Optional)
          </label>
          <textarea
            value={formData.workflowRules || ''}
            onChange={(e) => setFormData({ ...formData, workflowRules: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="e.g., This evidence is required if property is in SFHA"
            rows={4}
          />
          <p className="text-xs text-gray-500 mt-1">
            💡 Describe when this evidence is required or optional
          </p>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 bg-white flex justify-end gap-2">
        <button onClick={onCancel} className="btn-secondary px-4 py-2 text-sm">
          Cancel
        </button>
        <button onClick={handleSave} className="btn-primary px-4 py-2 text-sm">
          Save Changes
        </button>
      </div>
    </div>
  );
};

// Field Edit Panel Component
interface FieldEditPanelProps {
  itemIndex: number;
  evidenceIndex: number;
  fieldIndex: number;
  field?: EvidenceFieldDefinition;
  onSave: (field: EvidenceFieldDefinition) => void;
  onCancel: () => void;
}

const FieldEditPanel: React.FC<FieldEditPanelProps> = ({ field, onSave, onCancel }) => {
  const [formData, setFormData] = useState<FieldEditFormData>({
    key: field?.key || '',
    label: field?.label || '',
    type: field?.type || 'string',
    required: field?.required ?? false,
    fileNameKey: field?.fileNameKey || '',
    fileNameKeys: field?.fileNameKeys || [],
    workflowRules: field?.workflowRules || '',
    enumValues: field?.enumValues || [],
    extractionHints: field?.extractionHints || {},
    normalizationRules: field?.normalizationRules || {},
    validationRules: field?.validationRules || {}
  });

  // Update form data when field prop changes (when switching between different field edits)
  // Use a ref to track the previous field to avoid unnecessary updates
  const prevFieldRef = useRef<string>('');
  useEffect(() => {
    // Create a unique identifier for the field to detect changes
    const fieldId = field ? `${field.key}-${JSON.stringify(field.extractionHints)}-${JSON.stringify(field.normalizationRules)}-${JSON.stringify(field.validationRules)}` : '';
    
    if (fieldId !== prevFieldRef.current) {
      prevFieldRef.current = fieldId;
      
      if (field) {
        setFormData({
          key: field.key || '',
          label: field.label || '',
          type: field.type || 'string',
          required: field.required ?? false,
          fileNameKey: field.fileNameKey || '',
          fileNameKeys: field.fileNameKeys || [],
          workflowRules: field.workflowRules || '',
          enumValues: field.enumValues ? [...field.enumValues] : [],
          extractionHints: field.extractionHints ? { ...field.extractionHints } : {},
          normalizationRules: field.normalizationRules ? { ...field.normalizationRules } : {},
          validationRules: field.validationRules ? { ...field.validationRules } : {}
        });
      } else {
        // Reset form if field is undefined
        setFormData({
          key: '',
          label: '',
          type: 'string',
          required: false,
          fileNameKey: '',
          fileNameKeys: [],
          workflowRules: '',
          enumValues: [],
          extractionHints: {},
          normalizationRules: {},
          validationRules: {}
        });
      }
    }
  }, [field]);

  const handleSave = () => {
    if (!formData.key.trim() || !formData.label.trim()) {
      alert('Key and Label are required');
      return;
    }
    
    // Clean up empty objects and arrays, but preserve boolean false values
    // Normalize keywords/anchors from string (raw input) to array when saving
    const toExtractionArr = (v: string | string[] | undefined): string[] => Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    const extractionHints = formData.extractionHints;
    const kwArr = extractionHints ? toExtractionArr(extractionHints.keywords) : [];
    const anArr = extractionHints ? toExtractionArr(extractionHints.anchors) : [];
    const hasExtractionHints = (kwArr.length > 0) || (anArr.length > 0) || ((extractionHints?.regex?.trim()?.length ?? 0) > 0);
    
    const cleanedExtractionHints = hasExtractionHints && extractionHints ? {
      ...(kwArr.length > 0 && { keywords: kwArr }),
      ...(anArr.length > 0 && { anchors: anArr }),
      ...(extractionHints.regex && extractionHints.regex.trim().length > 0 && { regex: extractionHints.regex.trim() })
    } : undefined;
    
    // Preserve normalizationRules even if values are false (boolean false is a valid value)
    const cleanedNormalizationRules = formData.normalizationRules && (
      formData.normalizationRules.dateFormat ||
      formData.normalizationRules.trim === true ||
      formData.normalizationRules.uppercase === true ||
      formData.normalizationRules.lowercase === true
    ) ? {
      ...(formData.normalizationRules.dateFormat && { dateFormat: formData.normalizationRules.dateFormat }),
      ...(formData.normalizationRules.trim !== undefined && { trim: formData.normalizationRules.trim }),
      ...(formData.normalizationRules.uppercase !== undefined && { uppercase: formData.normalizationRules.uppercase }),
      ...(formData.normalizationRules.lowercase !== undefined && { lowercase: formData.normalizationRules.lowercase })
    } : undefined;
    
    // Preserve validationRules if any value is set
    const cleanedValidationRules = formData.validationRules && (
      formData.validationRules.min !== undefined ||
      formData.validationRules.max !== undefined ||
      formData.validationRules.minLength !== undefined ||
      formData.validationRules.maxLength !== undefined ||
      formData.validationRules.pattern ||
      formData.validationRules.mustBeLessThan ||
      formData.validationRules.mustBeGreaterThan ||
      formData.validationRules.customValidation
    ) ? {
      ...(formData.validationRules.min !== undefined && { min: formData.validationRules.min }),
      ...(formData.validationRules.max !== undefined && { max: formData.validationRules.max }),
      ...(formData.validationRules.minLength !== undefined && { minLength: formData.validationRules.minLength }),
      ...(formData.validationRules.maxLength !== undefined && { maxLength: formData.validationRules.maxLength }),
      ...(formData.validationRules.pattern && { pattern: formData.validationRules.pattern }),
      ...(formData.validationRules.mustBeLessThan && { mustBeLessThan: formData.validationRules.mustBeLessThan }),
      ...(formData.validationRules.mustBeGreaterThan && { mustBeGreaterThan: formData.validationRules.mustBeGreaterThan }),
      ...(formData.validationRules.customValidation && { customValidation: formData.validationRules.customValidation })
    } : undefined;
    
    // Handle fileNameKeys: parse from string (raw input) or array; then store as fileNameKeys array or fileNameKey (backward compat)
    const toFileNameKeysArray = (v: string | string[] | undefined): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.filter(k => k && k.trim().length > 0).map(k => k.trim());
      return String(v).split(',').map(k => k.trim()).filter(Boolean);
    };
    const fileNameKeysArr = toFileNameKeysArray(formData.fileNameKeys);
    const fileNameKey = formData.fileNameKey?.trim() || undefined;
    const fileNameKeys = fileNameKeysArr.length > 0 ? fileNameKeysArr : (fileNameKey ? [fileNameKey] : []);
    const toEnumValuesArray = (v: string | string[] | undefined): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.filter(Boolean).map(k => k.trim()).filter(Boolean);
      return String(v).split(',').map(k => k.trim()).filter(Boolean);
    };
    const enumValuesArr = formData.type === 'enum' ? toEnumValuesArray(formData.enumValues) : [];
    
    onSave({
      key: formData.key.trim(),
      label: formData.label.trim(),
      type: formData.type,
      required: formData.required,
      // Store as fileNameKeys if multiple, or fileNameKey if single (for backward compatibility)
      fileNameKeys: fileNameKeys.length > 1 ? fileNameKeys : undefined,
      fileNameKey: fileNameKeys.length === 1 ? fileNameKeys[0] : (fileNameKeys.length === 0 ? fileNameKey : fileNameKeys[0]),
      workflowRules: formData.workflowRules?.trim() || undefined,
      enumValues: formData.type === 'enum' && enumValuesArr.length > 0 ? enumValuesArr : undefined,
      extractionHints: cleanedExtractionHints,
      normalizationRules: cleanedNormalizationRules,
      validationRules: cleanedValidationRules,
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Field</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key *
            </label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="e.g., policyNumber"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label *
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="e.g., Policy Number"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ 
                ...formData, 
                type: e.target.value as EvidenceFieldDefinition['type'],
                enumValues: e.target.value === 'enum' ? [] : undefined
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="string">String</option>
              <option value="date">Date</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="enum">Enum</option>
              <option value="list">List</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                className="w-4 h-4"
              />
              Required
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            File Name Key(s) (Optional)
          </label>
          <input
            type="text"
            value={(() => {
              const fk = formData.fileNameKeys;
              if (typeof fk === 'string') return fk;
              if (Array.isArray(fk) && fk.length > 0) return fk.join(', ');
              return formData.fileNameKey || '';
            })()}
            onChange={(e) => {
              setFormData({
                ...formData,
                fileNameKeys: e.target.value
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="e.g., loan-number, application-number (comma-separated for multiple)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter one or more keys separated by commas. Files matching any key will be processed.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Workflow Rules (Optional)
          </label>
          <textarea
            value={formData.workflowRules || ''}
            onChange={(e) => setFormData({ ...formData, workflowRules: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="e.g., if loanNumber AND borrowerName are present THEN yes ELSE no"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Natural language rule to derive value from asset facts. Use AI evaluation for complex rules (e.g. AND/OR).
          </p>
        </div>

        {formData.type === 'enum' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enum Values (comma-separated)
            </label>
            <input
              type="text"
              value={typeof formData.enumValues === 'string' ? formData.enumValues : (formData.enumValues?.join(', ') || '')}
              onChange={(e) => setFormData({ 
                ...formData, 
                enumValues: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Value1, Value2, Value3"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Extraction Hints - Keywords (Optional)
          </label>
          <input
            type="text"
            value={Array.isArray(formData.extractionHints?.keywords) ? formData.extractionHints.keywords.join(', ') : (formData.extractionHints?.keywords ?? '')}
            onChange={(e) => setFormData({ 
              ...formData, 
              extractionHints: {
                ...formData.extractionHints,
                keywords: e.target.value
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="keyword1, keyword2 (spaces allowed within a keyword)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Extraction Hints - Anchors (Optional)
          </label>
          <input
            type="text"
            value={Array.isArray(formData.extractionHints?.anchors) ? formData.extractionHints.anchors.join(', ') : (formData.extractionHints?.anchors ?? '')}
            onChange={(e) => setFormData({ 
              ...formData, 
              extractionHints: {
                ...formData.extractionHints,
                anchors: e.target.value
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="anchor1, anchor2 (spaces allowed)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Extraction Hints - Regex (Optional)
          </label>
          <input
            type="text"
            value={formData.extractionHints?.regex || ''}
            onChange={(e) => setFormData({ 
              ...formData, 
              extractionHints: {
                ...formData.extractionHints,
                regex: e.target.value || undefined
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Regex pattern (optional)"
          />
        </div>

        {/* Normalization Rules */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Normalization Rules (Optional)
          </label>
          <div className="space-y-2">
            {formData.type === 'date' && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date Format</label>
                <input
                  type="text"
                  value={formData.normalizationRules?.dateFormat || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    normalizationRules: {
                      ...formData.normalizationRules,
                      dateFormat: e.target.value || undefined
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g., YYYY-MM-DD, MM/DD/YYYY"
                />
              </div>
            )}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.normalizationRules?.trim || false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    normalizationRules: {
                      ...formData.normalizationRules,
                      trim: e.target.checked || undefined
                    }
                  })}
                  className="w-4 h-4"
                />
                Trim whitespace
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.normalizationRules?.uppercase || false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    normalizationRules: {
                      ...formData.normalizationRules,
                      uppercase: e.target.checked || undefined
                    }
                  })}
                  className="w-4 h-4"
                />
                Uppercase
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.normalizationRules?.lowercase || false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    normalizationRules: {
                      ...formData.normalizationRules,
                      lowercase: e.target.checked || undefined
                    }
                  })}
                  className="w-4 h-4"
                />
                Lowercase
              </label>
            </div>
          </div>
        </div>

        {/* Validation Rules */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Validation Rules (Optional)
          </label>
          <div className="space-y-2">
            {formData.type === 'number' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Min</label>
                  <input
                    type="number"
                    value={formData.validationRules?.min ?? ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      validationRules: {
                        ...formData.validationRules,
                        min: e.target.value ? Number(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Minimum value"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Max</label>
                  <input
                    type="number"
                    value={formData.validationRules?.max ?? ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      validationRules: {
                        ...formData.validationRules,
                        max: e.target.value ? Number(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Maximum value"
                  />
                </div>
              </div>
            )}
            {formData.type === 'string' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    value={formData.validationRules?.minLength ?? ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      validationRules: {
                        ...formData.validationRules,
                        minLength: e.target.value ? Number(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Minimum length"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    value={formData.validationRules?.maxLength ?? ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      validationRules: {
                        ...formData.validationRules,
                        maxLength: e.target.value ? Number(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Maximum length"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pattern (Regex)</label>
              <input
                type="text"
                value={formData.validationRules?.pattern || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  validationRules: {
                    ...formData.validationRules,
                    pattern: e.target.value || undefined
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Regex pattern (e.g., ^[A-Z0-9]+$)"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Must Be Less Than (field key)</label>
                <input
                  type="text"
                  value={formData.validationRules?.mustBeLessThan || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    validationRules: {
                      ...formData.validationRules,
                      mustBeLessThan: e.target.value || undefined
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g., closingDate"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Must Be Greater Than (field key)</label>
                <input
                  type="text"
                  value={formData.validationRules?.mustBeGreaterThan || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    validationRules: {
                      ...formData.validationRules,
                      mustBeGreaterThan: e.target.value || undefined
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g., openingDate"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Custom Validation Description</label>
              <input
                type="text"
                value={formData.validationRules?.customValidation || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  validationRules: {
                    ...formData.validationRules,
                    customValidation: e.target.value || undefined
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Custom validation rule description"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 bg-white flex justify-end gap-2">
        <button onClick={onCancel} className="btn-secondary px-4 py-2 text-sm">
          Cancel
        </button>
        <button onClick={handleSave} className="btn-primary px-4 py-2 text-sm">
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default ChecklistForm;

